import { ReactNode } from "react";
import { usePermission } from "~/hooks/usePermission";
import type { Action, ResourceType } from "~/api/permissions/api";

interface PermissionGateProps {
  resource: ResourceType;
  action: Action;
  resourceId?: string;
  resourceAttr?: Record<string, unknown>;
  /** Skip the check (and render nothing) until required data is available */
  skip?: boolean;
  /** Content to show while the permission check is loading */
  fallback?: ReactNode;
  children: ReactNode;
}

/**
 * Renders children only if the current user has the specified Cerbos permission.
 *
 * Replaces hardcoded role checks like `{role === "GXM_ADMIN" && <Button />}`
 * with a proper Cerbos-backed permission check.
 *
 * @example
 * <PermissionGate resource="employee" action="create" resourceAttr={{ entity_id: entityId }}>
 *   <CustomeButton label="Add Employee" />
 * </PermissionGate>
 */
function PermissionGate({
  resource,
  action,
  resourceId,
  resourceAttr,
  skip,
  fallback = null,
  children,
}: PermissionGateProps) {
  const { allowed, loading } = usePermission(
    resource,
    action,
    resourceId,
    resourceAttr,
    { skip },
  );

  if (loading) return <>{fallback}</>;
  if (!allowed) return null;

  return <>{children}</>;
}

export default PermissionGate;
